import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { WatermarkFlag } from '../../../watermarkUpsell/files/WatermarkFlag';
import { fileHasWatermark } from '../../../watermarkUpsell/files/watermarkedFile';
import { cardPicture } from '../assets/revisionCards';
import { kitPictureSrc } from '../../kitPictureSrc';
import { KitSlotView } from './kitSlots';
import { kitMessages } from './kitMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The width of the slot picture (Kit.module.scss `.thumb`, 140 px high, a 16:9 file at most 250 px). */
const SLOT_THUMB_PX = 260;

interface KitSlotThumbProps {
  view: KitSlotView;
  name: string;
  /** Opens the viewer; absent while the slot has no ready file. */
  onOpen?: () => void;
  /** A picture that is not a kit file (the cover of the song), as an absolute or relative url. */
  pictureUrl?: string | null;
  /** The text of an empty frame ("16:9 · full song"). */
  ghost?: string;
  /** A badge at the bottom left of the picture ("Linked", "Your file · 4K"). */
  badge?: string | null;
}

/**
 * The picture of a slot (design assets-rethink b1 `.ks-th`): the ready file with its revision chip,
 * the progress of a revision that is made now, or an empty frame.
 */
const KitSlotThumbComponent: React.FC<KitSlotThumbProps> = ({ view, name, onOpen, pictureUrl, ghost, badge }) => {
  const intl = useIntl();
  const card = view.readyCard === null ? null : cardPicture(view.readyCard);
  const picture = pictureUrl ?? (card === null ? null : kitPictureSrc(card, SLOT_THUMB_PX));
  const working = view.workingAsset;
  const hasWatermark = view.preview || view.readyAssets.some((asset) => fileHasWatermark(asset));
  const content = (
    <>
      {picture !== null ? (
        <img className={styles.thumbImage} src={picture} alt="" />
      ) : (
        <span className={styles.ghost}>{ghost ?? ''}</span>
      )}
      {view.readyAsset !== null && <span className={styles.revision}>r{view.readyAsset.version}</span>}
      {/* design: trial-upsell a4. The "Watermark" flag in the place of the silent preview icon. */}
      {hasWatermark && <WatermarkFlag />}
      {badge !== undefined && badge !== null && <span className={styles.thumbBadge}>{badge}</span>}
      {working !== null && view.state === 'making' && (
        <span className={styles.progress}>
          <span className={styles.progressBar}>
            <i style={{ width: `${view.progress}%` }} />
          </span>
          <span>
            {working.status === 'queued'
              ? intl.formatMessage(kitMessages.makingQueued)
              : intl.formatMessage(kitMessages.makingPercent, { percent: view.progress })}
          </span>
        </span>
      )}
    </>
  );
  return onOpen === undefined ? (
    <div className={styles.thumb}>{content}</div>
  ) : (
    <button
      type="button"
      className={styles.thumb}
      onClick={onOpen}
      aria-label={intl.formatMessage(kitMessages.open, { name })}
    >
      {content}
    </button>
  );
};

export const KitSlotThumb = TypedMemo(KitSlotThumbComponent);
